import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import Link from "next/link";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import WinnerDropBadge from "../winner/WinnerDropBadge";
import WaveDropTime from "../time/WaveDropTime";
import ApprovalStatusBadge from "@/components/waves/approval/ApprovalStatusBadge";
import { isOfficiallyApprovedDrop } from "@/helpers/waves/approve-wave.helpers";
import type { DropTimestampLayout } from "../drop.types";
interface ParticipationDropHeaderProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly winningThreshold?: number | null | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
}
export default function ParticipationDropHeader(t0) {
  const $ = _c(34);
  const {
    drop,
    showWaveInfo,
    winningThreshold,
    timestampLayout: t1
  } = t0;
  const timestampLayout = t1 === undefined ? "inline" : t1;
  const isStackedTimestamp = timestampLayout === "stacked";
  const authorIdentity = drop.author.handle ?? drop.author.primary_address;
  const isApproveDrop = typeof winningThreshold === "number" && winningThreshold > 0;
  const isApprovedDrop = isApproveDrop && isOfficiallyApprovedDrop(drop);
  const rank = drop.rank;
  const hasWinnerRank = typeof rank === "number" && rank !== 0 && !Number.isNaN(rank);
  let statusBadge = null;
  if (isApprovedDrop) {
    const t2 = drop.winning_context?.decision_time ?? null;
    let t3;
    if ($[0] !== t2) {
      t3 = <ApprovalStatusBadge approvedAt={t2} />;
      $[0] = t2;
      $[1] = t3;
    } else {
      t3 = $[1];
    }
    statusBadge = t3;
  } else {
    if (!isApproveDrop && hasWinnerRank) {
      const t2 = drop.winning_context?.decision_time ?? null;
      let t3;
      if ($[2] !== rank || $[3] !== t2) {
        t3 = <WinnerDropBadge rank={rank} decisionTime={t2} />;
        $[2] = rank;
        $[3] = t2;
        $[4] = t3;
      } else {
        t3 = $[4];
      }
      statusBadge = t3;
    }
  }
  const t2 = isStackedTimestamp ? "tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-gap-y-1" : "tw-flex tw-flex-wrap tw-items-center tw-gap-x-2";
  const t3 = `/${authorIdentity}`;
  let t4;
  if ($[5] !== authorIdentity || $[6] !== t3) {
    t4 = <p className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none"><Link onClick={_temp} href={t3} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{authorIdentity}</Link></p>;
    $[5] = authorIdentity;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== drop.author.level) {
    t5 = <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[8] = drop.author.level;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== isStackedTimestamp) {
    t6 = !isStackedTimestamp && <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" />;
    $[10] = isStackedTimestamp;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== drop.created_at || $[13] !== isStackedTimestamp) {
    t7 = !isStackedTimestamp && <WaveDropTime timestamp={drop.created_at} />;
    $[12] = drop.created_at;
    $[13] = isStackedTimestamp;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== statusBadge || $[16] !== t4 || $[17] !== t5 || $[18] !== t6 || $[19] !== t7) {
    t8 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{t4}{t5}{statusBadge}{t6}{t7}</div>;
    $[15] = statusBadge;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== drop.created_at || $[22] !== isStackedTimestamp) {
    t9 = isStackedTimestamp && <WaveDropTime timestamp={drop.created_at} />;
    $[21] = drop.created_at;
    $[22] = isStackedTimestamp;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== t2 || $[25] !== t8 || $[26] !== t9) {
    t10 = <div className={t2}>{t8}{t9}</div>;
    $[24] = t2;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== drop.wave || $[29] !== showWaveInfo) {
    t11 = showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const isDirectMessage = waveMeta?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link onClick={_temp2} href={waveHref} className="tw-leading-0 tw-mb-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[28] = drop.wave;
    $[29] = showWaveInfo;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t10 || $[32] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
function _temp2(e_0) {
  return e_0.stopPropagation();
}
function _temp(e) {
  return e.stopPropagation();
}